/**
 * intakeValidators
 * ────────────────
 * Feb 2026 — Phase 3e.0 of B2 IntakeWizard extraction.
 *
 * Pure validators used by both legacy App.js (currently inline at
 * lines ~18157–18219) and V2 IntakeWizardContext.  Pure functions, no
 * React dependencies.  Each returns either `null` (valid) or a
 * user-facing error string.
 *
 * Phase 3e.0 promise: legacy is provably unchanged.  This file is
 * created and consumed by V2 only; App.js will migrate to it in
 * Phase 3e.3 cleanup.
 */

const calculateAge = (dob) => {
  if (!dob) return -1;
  const birth = new Date(dob);
  if (Number.isNaN(birth.getTime())) return -1;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age -= 1;
  return age;
};

export function validateEmail(email) {
  if (!email) return null; // Email is optional
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!re.test(email)) return 'Please enter a valid email address';
  return null;
}

export function validatePhone(phone) {
  if (!phone) return 'Phone number is required';
  const digitsOnly = String(phone).replace(/\D/g, '');
  if (digitsOnly.length === 10 || digitsOnly.length === 11) return null;
  return 'Please enter a valid phone number (10 digits)';
}

export function validateZipCode(zipcode) {
  if (!zipcode) return 'Zip code is required';
  const re = /^\d{5}(-?\d{4})?$/;
  if (!re.test(zipcode)) return 'Please enter a valid zip code (5 or 9 digits)';
  return null;
}

export function validateSSN(ssn) {
  if (!ssn) return null; // SSN is optional
  const re = /^\d{3}-\d{2}-\d{4}$/;
  if (!re.test(ssn)) return 'Please enter SSN in format XXX-XX-XXXX';
  return null;
}

export function validateDateOfBirth(dob) {
  if (!dob) return 'Date of birth is required';
  const date = new Date(dob);
  if (Number.isNaN(date.getTime())) return 'Please enter a valid date';
  const today = new Date();
  if (date > today) return 'Date of birth cannot be in the future';
  const age = calculateAge(dob);
  if (age < 0 || age > 120) return 'Please enter a valid date of birth';
  return null;
}

/**
 * Validate the 5 mandatory fields legacy App.js checks before
 * submitting the IntakeWizard form (lines 15342–15378).  Returns an
 * `{[fieldName]: errorMessage}` object — empty if all valid.
 */
export function validateIntakeRequiredFields(data) {
  const errors = {};
  const v = (k, fn) => {
    const e = fn(data?.[k]);
    if (e) errors[k] = e;
  };
  v('email', validateEmail);
  v('phone', validatePhone);
  v('zipcode', validateZipCode);
  v('ssn', validateSSN);
  v('date_of_birth', validateDateOfBirth);
  return errors;
}

export const __test_only_calculateAge = calculateAge;
